import Plot from "components/plotly/LocalisedPlot";
import { LegendHint } from "../LegendHint";
import { useTenantTranslation } from "hooks/formatters";
import { PlotData } from "plotly.js";

import {
  EditingMetric,
  getMetricTranslationKey,
  METRIC_COLORS,
} from "./constants";

type EditingSeries = {
  heat_date: string;
  product_group_name: string;
} & Partial<Record<EditingMetric, number | null>>;

type EditingMetricsPlotProps = Readonly<{
  series: readonly EditingSeries[];
  selectedProductGroup: string;
  selectedMetrics: readonly EditingMetric[];
}>;

export const EditingMetricsPlot = ({
  series,
  selectedProductGroup,
  selectedMetrics,
}: EditingMetricsPlotProps) => {
  const { t } = useTenantTranslation();

  const filteredSeries =
    selectedProductGroup === "all"
      ? series.filter((s) => s.product_group_name === "All")
      : series.filter((s) => s.product_group_name === selectedProductGroup);

  const sortedSeries = [...filteredSeries].sort(
    (a, b) => new Date(a.heat_date).getTime() - new Date(b.heat_date).getTime()
  );

  const dates = sortedSeries.map((s) => s.heat_date);

  const data: Partial<PlotData>[] = selectedMetrics
    .filter((metric) =>
      sortedSeries.some((s) => s[metric] !== null && s[metric] !== undefined)
    )
    .map((metric) => ({
      x: dates,
      y: sortedSeries.map((s) => s[metric] ?? null),
      type: "scatter",
      mode: "lines+markers",
      name: t(getMetricTranslationKey(metric)),
      line: {
        color: METRIC_COLORS[metric],
        width: 2,
      },
      marker: {
        size: 6,
        color: METRIC_COLORS[metric],
      },
    }));

  return (
    <>
      <Plot
        data={data}
        layout={{
          autosize: true,
          xaxis: {
            title: { text: t("date") },
            type: "date",
          },
          yaxis: {
            title: { text: t("averageCountPerHeat") },
          },
          margin: {
            l: 60,
            r: 30,
            t: 30,
            b: 60,
          },
          hovermode: "x unified",
          showlegend: true,
          legend: {
            x: 1.05,
            y: 1,
            xanchor: "left",
          },
        }}
        config={{
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
        }}
        style={{ width: "100%", height: "400px" }}
        useResizeHandler
      />
      <LegendHint />
    </>
  );
};
